<script setup lang="ts">
import { TrashIcon } from "@heroicons/vue/24/outline";
import { useField } from "@tanstack/vue-form";
import { useVModel } from "@vueuse/core";
import { debounce, uniq } from "lodash";
import Avatar from "primevue/avatar";
import Checkbox from "primevue/checkbox";
import Dropdown from "primevue/dropdown";
import InputText from "primevue/inputtext";
import Skeleton from "primevue/skeleton";
import TextArea from "primevue/textarea";
import { computed, defineEmits, reactive, ref, toRefs, watch } from "vue";
import { accountApplicationPresets } from "@/components/Account/styles";
import CloudinaryButton from "@/components/CloudinaryUploadWidget/index.vue";
import { useGetCitiesData } from "@/providers/cities/useGetCitiesData.ts";
import { useSearchCities } from "@/providers/cities/useSearchCities.ts";
import type { LocalState, Props } from "./types.ts";

const props = defineProps<Props>();
const { editable, parentForm, oktaId } = toRefs(props);

const localState = reactive<LocalState>({});

const emit = defineEmits(["update:modelValue"]);
const profile = useVModel(props, "modelValue", emit);

const profileCities = computed(() =>
  uniq([Number(profile.value?.location_city_id), Number(profile?.value?.work_location_city_id)].filter((val) => val)),
);

const profileCitiesFetchEnabled = computed(() => !!profileCities?.value?.length);
const { data: originalCites, isLoading: isLoadingOriginalCites } = useGetCitiesData({
  limit: 100,
  page: 1,
  city_ids: profileCities,
  in_use: false,
  useQueryOptions: { enabled: profileCitiesFetchEnabled },
});

const cityString = ref("");
const {
  data: cityOptions,
  isFetched: isFetchedCityLocation,
  isLoading: isLoadingCityLocation,
  isPending: isPendingCityLocation,
} = useSearchCities({
  limit: 100,
  skip: 0,
  search: cityString,
  useQueryOptions: { enabled: editable },
});

const fetchCityOptions = debounce((event) => {
  cityString.value = event.value;
}, 500);

const cityWorkString = ref("");
const {
  data: cityWorkLocationOptions,
  isLoading: isLoadingCityWorkLocation,
  isPending: isPendingCityWorkLocation,
} = useSearchCities({
  limit: 100,
  skip: 0,
  search: cityWorkString,
  useQueryOptions: { enabled: editable },
});

const fetchOptionCityWorkLocationOptions = debounce((event) => {
  cityWorkString.value = event.value;
}, 500);

const cityOptionsAndCurrent = computed(() => {
  const retval = [...(cityOptions?.value ?? [])];
  if (localState.locationCity) {
    const foundIndex = retval.findIndex((val) => localState?.locationCity?.id === val.id);
    if (foundIndex >= 0) {
      retval.unshift(retval.splice(foundIndex, 1)[0]);
    } else {
      retval.unshift(localState.locationCity);
    }
  }

  return retval.map((value) => ({
    ...value,
    showAll: cityString.value,
  }));
});

const cityWorkOptionsAndCurrent = computed(() => {
  const retval = [...(cityWorkLocationOptions?.value ?? [])];
  if (localState.workLocationCity) {
    const foundIndex = retval.findIndex((val) => localState?.workLocationCity?.id === val.id);
    if (foundIndex >= 0) {
      retval.unshift(retval.splice(foundIndex, 1)[0]);
    } else {
      retval.unshift(localState.workLocationCity);
    }
  }

  return retval.map((value) => ({
    ...value,
    showAll: cityWorkString.value,
  }));
});

const sameAsLocationString = computed(() => (localState.sameAsLocation ? "Same as Above" : ""));
const updateSameAsLocation = () => {
  localState.sameAsLocation = !localState.sameAsLocation;
  updateWorkLocation(true);
};

const updateWorkLocation = (reset = false) => {
  if (localState.sameAsLocation) {
    if (profile?.value?.location_city_id) {
      profile.value.work_location_city_id = profile?.value?.location_city_id;
      localState.workLocationCity = localState.locationCity;
      workLocationCityIdField.api.handleChange(profile?.value?.location_city_id);
    } else {
      // If the same as local city button is selected and the user clears the location dropdown
      localState.sameAsLocation = false;
      profile.value.work_location_city_id = undefined;
      localState.workLocationCity = undefined;
      workLocationCityIdField.api.handleChange(null);
    }
  } else if (reset) {
    // If the user unclicks the same as local city option. Clear the work location dropdown.
    profile.value.work_location_city_id = undefined;
    localState.workLocationCity = undefined;
    workLocationCityIdField.api.handleChange(null);
  }
};

watch(originalCites, (originalCites) => {
  localState.locationCity = originalCites?.find((item) => String(item?.id) === profile?.value?.location_city_id);
  localState.workLocationCity = originalCites?.find(
    (item) => String(item?.id) === profile?.value?.work_location_city_id,
  );
});

watch(
  [() => profile.value.location_city_id, () => profile.value.work_location_city_id, () => editable.value],
  ([newLCId, newWLCId]) => {
    if (newLCId && newLCId === newWLCId) {
      localState.sameAsLocation = true;
      localState.workLocationCity = localState.locationCity;
    } else {
      localState.sameAsLocation = false;
      if (!newWLCId) {
        localState.workLocationCity = undefined;
      }
    }
  },
  { immediate: true },
);

const bioField = useField({ name: "bio", form: parentForm.value });
const jobTitleField = useField({ name: "job_title", form: parentForm.value });
const departmentField = useField({ name: "department", form: parentForm.value });
const locationCityIdField = useField({ name: "location_city_id", form: parentForm.value });
const workLocationCityIdField = useField({ name: "work_location_city_id", form: parentForm.value });
const headshotField = useField({ name: "headshot", form: parentForm.value });

const removeHeadShot = () => {
  if (profile.value) {
    profile.value.headshot = null;
    headshotField.api.handleChange(null);
  }
};
</script>

<template>
  <div class="grid grid-cols-3 gap-y-2 mt-2">
    <parentForm.Subscribe>
      <template v-slot="{ canSubmit, isSubmitting }">
        <div class="min-h-12 flex items-center">
          <label for="jobTitle" class="font-semibold text-lg min-w-0 break-words hyphens-auto block">Job Title</label>
        </div>
        <div class="col-span-2 flex items-center">
          <div v-if="!editable" class="">{{ profile.job_title }}</div>
          <div v-else-if="editable && !isSubmitting" class="w-full">
            <InputText id="jobTitle" class="w-full" type="text" v-model="profile.job_title"
                       @blur="jobTitleField.api.handleBlur"
                       @update:model-value="jobTitleField.api.handleChange"
                       :invalid="!jobTitleField.state.value.meta.isValid"
            />
            <div class="common-error-text" v-show="!jobTitleField.state.value.meta.isValid">{{
                jobTitleField.state.value.meta?.errors?.map((error) => error?.message).join(" ")
              }}</div>
          </div>
          <skeleton v-else />
        </div>

        <div class="min-h-12 flex items-center">
          <label for="department" class="font-semibold text-lg min-w-0 break-words hyphens-auto block">Department</label>
        </div>
        <div class="col-span-2 flex items-center">
          <div v-if="!editable" class="">{{ profile.department }}</div>
          <div v-else-if="editable && !isSubmitting" class="w-full">
            <InputText id="department" class="w-full" type="text" v-model="profile.department"
                       @blur="departmentField.api.handleBlur"
                       @update:model-value="departmentField.api.handleChange"
                       :invalid="!departmentField.state.value.meta.isValid"
            />
            <div class="common-error-text" v-show="!departmentField.state.value.meta.isValid">{{
                departmentField.state.value.meta?.errors?.map((error) => error?.message).join(" ")
              }}</div>
          </div>
          <skeleton v-else />
        </div>

        <div class="min-h-12 flex items-center">
          <label class="font-semibold text-lg min-w-0 break-words hyphens-auto block">Location: City</label>
        </div>
        <div class="col-span-2 flex items-center">
          <div v-if="!editable && !isLoadingOriginalCites" class="">{{ localState.locationCity?.city_ascii ?? "" }}</div>
          <div v-else-if="editable && !isSubmitting" class="w-full">
            <Dropdown
              v-model="profile.location_city_id"
              :options="cityOptionsAndCurrent"
              option-label="city_ascii"
              :option-value="(val) => String(val.id)"
              placeholder="Select A City"
              class="w-full md:w-20rem"
              aria-label="Cities A Options"
              :filter="true"
              :filterFields="['showAll']"
              :show-clear="true"
              :resetFilterOnHide="true"
              :loading="isLoadingCityLocation"
              @filter="fetchCityOptions"
              @change="() => updateWorkLocation()"
              @update:model-value="(value) => locationCityIdField.api.handleChange(value ?? null)"
            >
              <template v-slot:option="{option}">
                <div>
                  <div class="font-semibold">{{ option.city_ascii }}</div>
                  <div>
                    {{ option.country?.name }}
                    <span v-if="option.country?.name && option.admin_name_ascii">-</span>
                    {{ option.admin_name_ascii }}
                  </div>
                </div>
              </template>
              <template v-slot:emptyfilter>
                <div v-if="isLoadingCityLocation || isPendingCityLocation">Loading ...</div>
                <div v-else>No Results Found</div>
              </template>
            </Dropdown>
          </div>
          <skeleton v-else class="w-full"/>
        </div>
        <div class="min-h-12 flex items-center">
          <label class="font-semibold text-lg min-w-0 break-words hyphens-auto block">Location: Country</label>
        </div>
        <div class="col-span-2 flex items-center">
          <div v-if="!isSubmitting && !isLoadingOriginalCites" class="">{{ localState.locationCity?.country?.name ?? "" }}</div>
          <skeleton v-else />
        </div>

        <div class="min-h-12 flex items-center">
          <label class="font-semibold text-lg min-w-0 break-words hyphens-auto block">Work Location</label>
        </div>
        <div class="col-span-2 flex items-center">
          <div v-if="!editable" class="">{{ sameAsLocationString }}</div>
          <div v-else-if="editable && !isSubmitting" class="w-full">
            <Checkbox :pt="accountApplicationPresets.checkbox" :model-value="localState.sameAsLocation" @change="updateSameAsLocation" :binary="true" inputId="sameAs" name="sameAs" value="Same as location above" />
            <label for="tenantManager" class="ml-3">Same as location above</label>
          </div>
          <skeleton v-else />
        </div>
        <div class="min-h-12 flex items-center">
          <label class="font-semibold text-lg min-w-0 break-words hyphens-auto block">Work Location: City</label>
        </div>
        <div class="col-span-2 flex items-center">
          <div v-if="!editable && !isLoadingOriginalCites" class="">{{ localState.workLocationCity?.city_ascii ?? "" }}</div>
          <div v-else-if="editable && !isSubmitting" class="w-full">
            <Dropdown
              v-model="profile.work_location_city_id"
              :options="cityWorkOptionsAndCurrent"
              option-label="city_ascii"
              :option-value="(val) => String(val.id)"
              placeholder="Select A City"
              class="w-full md:w-20rem"
              aria-label="Cities A Options"
              :filter="true"
              :filter-match-mode="undefined"
              :filter-fields="['showAll']"
              :show-clear="true"
              :loading="isLoadingCityWorkLocation"
              @filter="fetchOptionCityWorkLocationOptions"
              :disabled="localState.sameAsLocation"
              :resetFilterOnHide="true"
              @update:model-value="(value) => workLocationCityIdField.api.handleChange(value ?? null)"
            >
              <template v-slot:option="{option}">
                <div>
                  <div class="font-semibold">{{ option.city_ascii }}</div>
                  <div>
                    {{ option.country?.name }}
                    <span v-if="option.country?.name && option.admin_name_ascii">-</span>
                    {{ option.admin_name_ascii }}
                  </div>
                </div>
              </template>
              <template v-slot:emptyfilter>
                <div v-if="isLoadingCityWorkLocation || isPendingCityWorkLocation">Loading ...</div>
                <div v-else>No Results Found</div>
              </template>
            </Dropdown>
          </div>
          <skeleton v-else class="w-full"/>
        </div>
        <div class="min-h-12 flex items-center">
          <label class="font-semibold text-lg min-w-0 break-words hyphens-auto block">Work Location: Country</label>
        </div>
        <div class="col-span-2 flex items-center">
          <div v-if="!isSubmitting && !isLoadingOriginalCites" class="">{{ localState.workLocationCity?.country?.name ?? "" }}</div>
          <skeleton v-else />
        </div>

        <div class="min-h-12 flex items-center">
          <label class="font-semibold text-lg min-w-0 break-words hyphens-auto block">Headshot</label>
        </div>
        <div class="col-span-2">
          <Avatar v-if="!editable" :image="profile.headshot || '/images/icon-profile.svg'" class="mr-2 h-24 w-24" size="xlarge" shape="circle" />
          <div v-else-if="editable && !isSubmitting">
            <div v-if="profile.headshot" class="relative h-24 w-24">
              <Avatar :image="profile.headshot || '/images/icon-profile.svg'" class="mr-2 h-24 w-24" size="xlarge" shape="circle"/>
              <button
                @click="removeHeadShot"
                class="absolute top-0 right-0 w-8 h-8 bg-white rounded-full border border-gray-400 hover:text-red-100 hover:scale-125 duration-150"
              >
                <TrashIcon class="w-5 h-5 mx-auto" />
              </button>
            </div>
            <CloudinaryButton
              folder="profiles"
              placeholder="UPLOAD"
              :public-id-prefix="oktaId"
              @uploaded-media-data="(payload) => {
                profile.headshot = payload.secure_url;
                headshotField.api.handleChange(payload.secure_url ?? undefined)
              }"
            />
          </div>
          <skeleton shape="circle" size="6rem" v-else />
        </div>

        <div class="min-h-12 flex items-center">
          <label class="font-semibold text-lg min-w-0 break-words hyphens-auto block">Bio</label>
        </div>
        <div class="col-span-2 flex items-center">
          <div v-if="!editable" class="">{{ profile.bio }}</div>
          <div v-else-if="editable && !isSubmitting" class="w-full">
            <TextArea class="w-full" rows="5" cols="30" type="text" v-model="profile.bio"
                      @update:model-value="(value) => bioField.api.handleChange(value)"  />
            <div class="common-error-text" v-show="!bioField.state.value.meta.isValid">{{
                bioField.state.value.meta?.errors?.map((error) => error?.message).join(" ")
              }}</div>
          </div>
          <skeleton height="100px" v-else />
        </div>
      </template>
    </parentForm.Subscribe>
  </div>
</template>

<style scoped>

</style>
